import * as React from "react"
import request from "../util/request"

import sleep from "@util/sleep"
import * as Money from "../util/money"
import { RecipientAmountCheckbox } from "./BulkCreditRecipientForm/RecipientAmountCheckbox"
import { RecipientContainer } from "./BulkCreditRecipientForm/RecipientContainer"
import { RecipientCustomAmountInput } from "./BulkCreditRecipientForm/RecipientCustomAmountInput"
import Header from "./BulkCreditRecipientForm/Header"

interface Props {
  tripId: number
  tripHidingPendingPassengers: boolean
  bulkCreditId: number
  bulkCreditAmount: number
  bulkCreditTotalFormatted: string
  bulkCreditName: string
  bulkCreditPaymentType: string
  isV2: boolean
  recipientsCountIfEvenSplit?: number
  recipientSummaries: RecipientSummaryProps[]
  editBulkCreditTypePath: string
}

export interface RecipientSummaryProps {
  bulk_credit_recipient_id: number
  allocated_amount?: number
  participation_id: number
  recipient_name: string
  recipient_status: string | null
  package_name: string
  account_balance: number
}

interface State {
  recipientStates: RecipientState[]
}

export interface RecipientState {
  id: number
  allocatedAmount?: number
  error?: string
  isAutosaveInProgress: boolean
}

export interface RecipientStatesByInitial {
  [initial: string]: RecipientState[]
}

export default class BulkCreditRecipientForm extends React.Component<Props, State> {
  constructor(props) {
    super(props)

    this.state = { recipientStates: this.buildRecipientStates(props) }
  }

  buildRecipientStates(props: Props): RecipientState[] {
    const recipientStates: RecipientState[] = []
    props.recipientSummaries.forEach((rs) => {
      recipientStates.push({
        id: rs.bulk_credit_recipient_id,
        allocatedAmount: rs.allocated_amount,
        isAutosaveInProgress: false,
      })
    })
    return recipientStates
  }

  render() {
    return (
      <div className="grid gap-block-4 mt-4">
        <Header {...this.props} isDisabled={this.isDisabled()} disabledReason={this.disabledReason()} />
        <section className="section-setup">
          <h2 className="p-4 mb-0 border-b">Travelers</h2>
          <div className="table">{this.state.recipientStates.map(this.renderPassengerBox)}</div>
        </section>
      </div>
    )
  }
  isDisabled = (): boolean => {
    if (this.isEvenSplitBulkCredit()) {
      return this.totalRecipientsRemaining() !== 0
    } else {
      return this.props.bulkCreditAmount !== this.currentAllocatedTotalCents()
    }
  }

  disabledReason = (): JSX.Element => {
    const { bulkCreditAmount } = this.props
    const overageCents: number = bulkCreditAmount - this.currentAllocatedTotalCents()

    if (this.isEvenSplitBulkCredit()) {
      return (
        <div className="inline-block fg-black">
          Distribute another <span className="font-bold">{this.totalRecipientsRemaining()}</span> credits
        </div>
      )
    } else if (overageCents < 0) {
      return (
        <div className="inline-block font-bold">
          You've distributed
          <span className="text-red-700"> {Money.formatCentsAsUsd(-overageCents)}</span> more than you have.
        </div>
      )
    } else if (overageCents > 0) {
      return (
        <div className="inline-block font-bold">
          Distribute another <span>{Money.formatCentsAsUsd(overageCents)}</span>
        </div>
      )
    } else {
      return <div className="inline-block font-bold">Saving...</div>
    }
  }

  renderPassengerBox = (recipientState: RecipientState) => {
    const recipientSummary = this.props.recipientSummaries.find(
      (summary) => summary.bulk_credit_recipient_id === recipientState.id
    )

    const tripHidingPendingPassengers = this.props.tripHidingPendingPassengers;

    let inputElement

    if (this.isEvenSplitBulkCredit()) {
      inputElement = (
        <RecipientAmountCheckbox
          checked={this.isRecipientChecked(recipientState)}
          onChange={(event) => {
            this.handleCheckboxChange(event, recipientState)
          }}
          amountCents={this.evenSplitAmountPerRecipient()}
        />
      )
    } else {
      inputElement = (
        <RecipientCustomAmountInput
          value={this.getFormattedRecipientAmount(recipientState)}
          onValueChange={(value) => this.changePassengerAmount(value, recipientState)}
        />
      )
    }

    return (
      <RecipientContainer key={recipientState.id} recipientState={recipientState} recipientSummary={recipientSummary} tripHidingPendingPassengers={tripHidingPendingPassengers}>
        {inputElement}
      </RecipientContainer>
    )
  }

  isEvenSplitBulkCredit = () => this.props.recipientsCountIfEvenSplit != undefined

  currentAllocatedTotalCents: () => number = () =>
    this.state.recipientStates.map((state) => state.allocatedAmount || 0).reduce((acc, current) => acc + current)

  totalRecipientsRemaining: () => number = () =>
    this.props.recipientsCountIfEvenSplit -
    this.state.recipientStates.filter((state) => state.allocatedAmount != undefined).length

  isRecipientChecked: (RecipientState) => boolean = (r: RecipientState) => r.allocatedAmount != undefined

  updateRecipientState: (RecipientState) => void = (r) => {
    let states = this.state.recipientStates
    const idx = states.findIndex((target) => target.id === r.id)
    states[idx] = r
    this.setState({ recipientStates: states })
  }

  handleCheckboxChange = async (event, r: RecipientState) => {
    const isChecked = event.target.checked

    let newState: RecipientState
    if (isChecked === true) {
      newState = { ...r, allocatedAmount: this.evenSplitAmountPerRecipient() }
    } else {
      newState = { ...r, allocatedAmount: undefined }
    }

    await this.persistRecipient(newState)
    this.updateRecipientState(newState)
  }

  evenSplitAmountPerRecipient: () => number = () => {
    const recipientCount = this.props.recipientsCountIfEvenSplit
    const amountPerRecipient = this.props.bulkCreditAmount / recipientCount
    return Math.round(amountPerRecipient)
  }

  changePassengerAmount = async (value: string, r: RecipientState) => {
    const newAmountCents: number = Money.parseDollarsToCents(value)
    let newState = { ...r, allocatedAmount: newAmountCents }
    await this.persistRecipient(newState)
  }

  getFormattedRecipientAmount: (RecipientState) => string = (recipientState: RecipientState) => {
    return Money.formatCentsAsUsd(recipientState.allocatedAmount, undefined)
  }

  persistRecipient = async (r: RecipientState) => {
    this.updateRecipientState({ ...r, isAutosaveInProgress: true })

    const tripId = this.props.tripId
    try {
      let result = await request("PUT", `/api/v1/trips/${tripId}/bulk_credit_recipients/${r.id}`, {
        // Ecto Changesets expect an empty string instead of null if the value should be nil
        allocated_amount: r.allocatedAmount || "",
      })

      await sleep(500)

      if (result.status === "error") {
        this.updateRecipientState({ ...r, error: result.reason, isAutosaveInProgress: false })
      } else {
        this.updateRecipientState({ ...r, error: undefined, isAutosaveInProgress: false })
      }
    } catch (error) {
      this.updateRecipientState({ ...r, error: "Internal Error", isAutosaveInProgress: false })
    }
  }
}
